import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { ConversationEvent } from "@/types/conversation"
import { ApiError, getJson, postJson } from "@/utils/api"
import { Cable } from "@/utils/cable"

interface EventsResponse {
  events: ConversationEvent[]
  next_cursor?: string
  has_more: boolean
}

export function useConversationEvents(
  conversationId?: number,
  wsUrl?: string,
  sessionToken?: string,
) {
  const [events, setEvents] = useState<ConversationEvent[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  // Watermark separating historical backlog from live events. `null` while we're
  // still hydrating (history streaming in); a cursor string once the line is
  // drawn — events with `cursor > liveCursor` are live and earn the reveal
  // ceremony. See markHydrated.
  const [liveCursor, setLiveCursor] = useState<string | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [paused, setPaused] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  const [wsConnected, setWsConnected] = useState(false)
  const emptyPollsRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const pollingRef = useRef(false)
  // Refs to avoid stale closures inside the polling loop
  const cursorRef = useRef<string | undefined>(undefined)
  const conversationIdRef = useRef<number | undefined>(conversationId)
  const pausedRef = useRef<boolean>(paused)
  const seenEventIdsRef = useRef<Set<string>>(new Set())
  const cableRef = useRef<Cable | null>(null)
  const subRef = useRef<{ unsubscribe: () => void } | null>(null)

  // Keep refs in sync with state
  useEffect(() => {
    cursorRef.current = cursor
  }, [cursor])

  useEffect(() => {
    conversationIdRef.current = conversationId
  }, [conversationId])

  useEffect(() => {
    pausedRef.current = paused
  }, [paused])

  const fetchOnce = useCallback(async () => {
    const cid = conversationIdRef.current
    if (!cid) return
    const qs = new URLSearchParams()
    const cur = cursorRef.current
    if (cur) qs.set("after", cur)
    qs.set("limit", "50")
    const path = `/api/conversations/${cid}/events?${qs.toString()}`
    let res: EventsResponse
    try {
      res = await getJson<EventsResponse>(path)
    } catch (e) {
      // A 404 means the conversation is gone — it will never come back, so stop
      // polling instead of retrying forever. Other errors (network blips, 5xx)
      // propagate so the caller's backoff can retry.
      if (e instanceof ApiError && e.status === 404) {
        stop()
        // Flip the ref synchronously: setPaused() won't flush before the
        // polling loop checks pausedRef and would otherwise reschedule one more
        // tick, undoing stop(). The state setter keeps React in sync.
        pausedRef.current = true
        setPaused(true)
        setLastError("conversation_not_found")
        return
      }
      throw e
    }
    if (res.events?.length) {
      const seen = seenEventIdsRef.current
      const fresh = res.events.filter((e) => {
        if (seen.has(e.event_id)) return false
        seen.add(e.event_id)
        return true
      })
      if (fresh.length) setEvents((prev) => [...prev, ...fresh])
      if (res.next_cursor) setCursor(res.next_cursor)
      emptyPollsRef.current = 0
    } else {
      // If we haven't seen any events yet and keep getting empties, pause after a few
      if (!cur) {
        emptyPollsRef.current += 1
        if (emptyPollsRef.current >= 3) {
          // Pause polling until manually resumed or a new message is sent
          stop()
          setPaused(true)
          setIsPolling(false)
          return res
        }
      }
    }
    return res
  }, [])

  // Pull the entire existing event history, page by page, before relying on the
  // socket for new events. Used when rejoining a conversation that already has a
  // backlog (resume / "jump in"): a single fetchOnce only loads the first page,
  // and the socket only delivers *new* events. Uses a local `after` cursor —
  // not the React-state cursor — so the loop advances synchronously between
  // pages. Dedup via seenEventIdsRef keeps it safe to run alongside the socket.
  const primeBacklog = useCallback(async () => {
    const cid = conversationIdRef.current
    if (!cid) return
    let after = cursorRef.current
    for (let page = 0; page < 100; page++) {
      const qs = new URLSearchParams()
      if (after) qs.set("after", after)
      qs.set("limit", "100")
      let res: EventsResponse
      try {
        res = await getJson<EventsResponse>(
          `/api/conversations/${cid}/events?${qs.toString()}`,
        )
      } catch (e) {
        setLastError((e as Error)?.message || "backlog_failed")
        return
      }
      if (res.events?.length) {
        const seen = seenEventIdsRef.current
        const fresh = res.events.filter((e) => {
          if (seen.has(e.event_id)) return false
          seen.add(e.event_id)
          return true
        })
        if (fresh.length) setEvents((prev) => [...prev, ...fresh])
        if (res.next_cursor) {
          after = res.next_cursor
          setCursor(res.next_cursor)
        }
      }
      if (!res.has_more) break
    }
    // The cursor through which we've drained history — the caller uses this as
    // the live watermark (events after it are live).
    return after
  }, [])

  // Draw the line between historical backlog and live events. Pass the backlog's
  // final cursor after a resume; pass nothing (→ "") for a fresh conversation so
  // every event counts as live.
  const markHydrated = useCallback((watermark?: string) => {
    setLiveCursor(watermark ?? "")
  }, [])

  const startPolling = useCallback(() => {
    if (!conversationIdRef.current || pollingRef.current) return
    pollingRef.current = true
    setPaused(false)
    setIsPolling(true)
    const tick = async () => {
      try {
        const res = await fetchOnce()
        // Faster while draining backlog; slower otherwise (with jitter)
        const base = res?.has_more ? 200 : 5000
        const jitter = Math.floor(Math.random() * 1000)
        const delay = base + jitter
        if (!pausedRef.current)
          timerRef.current = window.setTimeout(() => void tick(), delay)
      } catch (e) {
        setLastError((e as Error)?.message || "poll_failed")
        if (!pausedRef.current)
          timerRef.current = window.setTimeout(() => void tick(), 5000)
      }
    }
    void tick()
  }, [fetchOnce])

  const stop = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    pollingRef.current = false
    setIsPolling(false)
  }, [])

  // Reset state when conversation changes
  useEffect(() => {
    stop()
    setEvents([])
    setCursor(undefined)
    setLiveCursor(null)
    emptyPollsRef.current = 0
    setPaused(false)
    setLastError(null)
    seenEventIdsRef.current = new Set()
    // tear down websocket
    if (subRef.current) {
      try {
        subRef.current.unsubscribe()
      } catch {
        /* ignored: subscription may already be torn down */
      }
      subRef.current = null
    }
    if (cableRef.current) {
      // let it close naturally; a fresh Cable will be created below
      cableRef.current = null
    }
    setWsConnected(false)
  }, [conversationId])

  useEffect(() => {
    return () => stop()
  }, [stop])

  const resume = useCallback(() => {
    emptyPollsRef.current = 0
    setPaused(false)
    startPolling()
  }, [startPolling])

  // Track user message IDs for deduplication
  const userMessageIds = useMemo(() => {
    return new Set(
      events
        .filter((e) => e.type === "user_message")
        .map((e) => e.correlates?.client_msg_id)
        .filter((id): id is string => Boolean(id)),
    )
  }, [events])

  // Emit an event to the server
  const emitEvent = useCallback(
    async (type: string, payload: Record<string, unknown>) => {
      if (!conversationId) {
        console.warn("Cannot emit event without conversation ID")
        return
      }

      // Build the appropriate endpoint based on event type
      let endpoint = ""
      let body: Record<string, unknown> = payload

      switch (type) {
        // Coaching events
        case "coaching_prompt_selected":
          endpoint = `/api/conversations/${conversationId}/coaching/select_prompt`
          break
        case "coaching_acknowledged":
          endpoint = `/api/conversations/${conversationId}/coaching/acknowledge`
          break
        case "coaching_page_advanced":
          endpoint = `/api/conversations/${conversationId}/coaching/page`
          break
        case "coaching_skipped":
          endpoint = `/api/conversations/${conversationId}/coaching/skip`
          break

        // Help and interaction events
        case "help_requested":
          endpoint = `/api/conversations/${conversationId}/help`
          break
        case "surface_focus_changed":
          endpoint = `/api/conversations/${conversationId}/focus`
          break
        case "message_composition_started":
        case "message_composition_changed":
        case "message_composition_abandoned":
          endpoint = `/api/conversations/${conversationId}/composition`
          body = { ...payload, event_type: type }
          break
        case "coaching_surface_toggled":
          endpoint = `/api/conversations/${conversationId}/coaching_toggle`
          break

        // Session management
        case "session_configured":
          endpoint = `/api/conversations/${conversationId}/session_configure`
          break
        case "conversation_reset":
          endpoint = `/api/conversations/${conversationId}/reset`
          break
        case "conversation_ended":
          endpoint = `/api/conversations/${conversationId}/end`
          break

        default:
          console.warn(`Unknown event type: ${type}`)
          return
      }

      try {
        await postJson(endpoint, { ...body, client_ts: Date.now() })
      } catch (error) {
        console.error(`Failed to emit ${type} event:`, error)
        setLastError(`Failed to emit ${type} event`)
      }
    },
    [conversationId],
  )

  // Start ActionCable and subscribe when possible; fall back to polling otherwise
  useEffect(() => {
    // need all parameters
    if (!conversationIdRef.current || !wsUrl || !sessionToken) return
    // set up cable
    const cable = new Cable(wsUrl, { reconnect: true })
    cableRef.current = cable
    const sub = cable.subscribe(
      "ConversationsChannel",
      { id: conversationIdRef.current, token: sessionToken },
      {
        connected: () => {
          setWsConnected(true)
          // switch off polling on confirmed subscription
          stop()
        },
        rejected: () => {
          setWsConnected(false)
          // A ConversationsChannel rejection is terminal — the conversation is
          // missing or unauthorized, not a transient blip. Don't fall back to an
          // infinite HTTP poll; pause instead. (A surviving conversation that
          // momentarily drops the socket comes through `disconnected`, not here.)
          stop()
          setPaused(true)
        },
        disconnected: () => {
          setWsConnected(false)
          // resume polling until socket reconnects
          if (!isPolling) startPolling()
        },
        received: (msg: unknown) => {
          const e = msg as ConversationEvent
          if (!e?.event_id) return
          const seen = seenEventIdsRef.current
          if (seen.has(e.event_id)) return
          seen.add(e.event_id)
          setEvents((prev) => [...prev, e])
          if (e.cursor) setCursor(e.cursor)
        },
      },
    )
    subRef.current = sub
    // ensure connection is opened
    cable.open()
    // Prime once via HTTP in case there is backlog before the socket starts delivering
    void fetchOnce()
    return () => {
      try {
        sub.unsubscribe()
      } catch {
        /* ignored: subscription may already be closed on unmount */
      }
    }
  }, [wsUrl, sessionToken, startPolling, stop, isPolling, fetchOnce])

  return {
    events,
    userMessageIds,
    emitEvent,
    startPolling,
    primeBacklog,
    markHydrated,
    liveCursor,
    stop,
    resume,
    isPolling,
    paused,
    lastError,
    wsConnected,
  }
}
